/**
 * b2bMarketplaceService.ts
 *
 * Multi-vendor B2B Marketplace — Auth & Client Resolution Layer
 *
 * Architecture overview
 * ─────────────────────
 * A single store owner (B2B Client) may be registered under MULTIPLE wholesale
 * companies that all operate on the same Axelio ERP backend.
 *
 * Login flow (mobile OTP app):
 *   1. User enters their email OR phone number.
 *   2. Supabase sends an OTP to that contact.
 *   3. After verification, we call `resolveB2bSession(email?, phone?)`.
 *   4. The function returns:
 *        a. An array of `B2bClientRecord` rows (one per company the user
 *           belongs to as a client).
 *        b. A deduplicated array of `company_id` strings — the "allowed
 *           companies" the user can order from.
 *   5. The mobile catalog queries:
 *        `inventory_products WHERE company_id IN (allowedCompanyIds)`
 *
 * Field-agent (internal employee) flow is handled separately — see
 * `resolveAgentSession()` below which checks `company_members`.
 */

import { supabase } from './supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single row from inventory_clients (one company's registration of this client). */
export interface B2bClientRecord {
  id:           string;
  company_id:   string;
  name:         string;
  email:        string | null;
  phone:        string | null;
  pricelist_id: string | null;
  /** Phase 34: business registration number (ח.פ.) for grouping branches */
  business_id?: string | null;
  is_vat_liable?: boolean;
  /** Phase 109: 'none' | 'view_only' | 'full' */
  client_access?: string;
  /** Phase 132: the client's consignment warehouse */
  consignment_warehouse_id?: string | null;
}

/** Full resolved session for a B2B client user. */
export interface B2bClientSession {
  /** All inventory_clients rows matching this user's email/phone. */
  clientRecords:    B2bClientRecord[];
  /** Deduplicated list of company_ids the user may browse / order from. */
  allowedCompanyIds: string[];
  /**
   * Convenience map: company_id → pricelist_id (or null).
   * The mobile catalog uses this to apply per-company pricing.
   */
  pricelistByCompany: Record<string, string | null>;
  /** Pre-fetched company names from the SECURITY DEFINER RPC (avoids RLS issues). */
  companies?: { id: string; legal_name: string }[];
}

/**
 * Resolved session for a Field Agent.
 *
 * Phase 16 adds `agentProfileId` — the inventory_agents.id of the logged-in
 * agent (NOT their auth.users.id).  This is the FK used in inventory_clients
 * as `agent_id`, so the client-selection view can query:
 *   inventory_clients WHERE agent_id = agentProfileId
 *
 * `agentName` is included for display in the portal header / client picker.
 *
 * Phase 37: `agentClientRecords` — all inventory_clients rows where
 *   agent_id = agentProfileId.  Populated at login/restore so the agent's
 *   BranchSwitcher is available immediately without an extra query.
 */
export interface AgentSession {
  userId:           string;   // auth.users.id
  companyId:        string;
  role:             string;
  modulePermissions: Record<string, string>;
  /** inventory_agents.id — null when the agent has no inventory_agents row */
  agentProfileId:   string | null;
  /** Display name from inventory_agents.name */
  agentName:        string | null;
  /** Agent's email from inventory_agents */
  agentEmail:       string | null;
  /** Agent's phone from inventory_agents */
  agentPhone:       string | null;
  /** sales_commission_rate from inventory_agents */
  salesCommissionRate:      number | null;
  /** collection_commission_rate from inventory_agents */
  collectionCommissionRate: number | null;
  /** Phase 132: agent's default (van) warehouse */
  defaultWarehouseId:       string | null;
  /** Phase 115: master agent can see all B2B clients, not just assigned ones */
  isMasterAgent: boolean;
  /** Phase 122: company IDs where this agent has is_master_agent = true */
  masterAgentCompanyIds: string[];
  /**
   * Phase 37: All inventory_clients rows assigned to this agent.
   * Phase 115: For master agents, includes ALL b2b_allowed clients for the company,
   * each tagged with is_own_client and assigned_agent_id/name.
   * Empty array when agentProfileId is null.
   */
  agentClientRecords: B2bClientRecord[];
}

// ─── B2B Client resolution ────────────────────────────────────────────────────

/**
 * resolveB2bSession
 *
 * Called immediately after a successful `supabase.auth.verifyOtp()`.
 * Pass the authenticated user's email and/or phone (from `session.user`).
 *
 * Returns null when no matching client records exist (user is not a B2B
 * client — might be an agent; call `resolveAgentSession` instead).
 *
 * @example
 * ```ts
 * const { data: { session } } = await supabase.auth.verifyOtp({
 *   email, token, type: 'email'
 * });
 * const user = session?.user;
 *
 * const b2bSession = await resolveB2bSession(
 *   user?.email ?? undefined,
 *   user?.phone ?? undefined,
 * );
 *
 * if (b2bSession) {
 *   // → redirect to B2B catalog, pass b2bSession via navigation state / context
 * } else {
 *   const agentSession = await resolveAgentSession(user!.id);
 *   if (agentSession) {
 *     // → redirect to agent dashboard
 *   } else {
 *     // → access denied
 *   }
 * }
 * ```
 */
export async function resolveB2bSession(
  email?: string,
  phone?: string,
): Promise<B2bClientSession | null> {
  if (!email && !phone) {
    console.warn('[b2bMarketplace] resolveB2bSession called with no email or phone');
    return null;
  }

  // Skip synthetic @b2b-sms.axelio.app emails — they won't match any real client record
  const effectiveEmail = email && !email.endsWith('@b2b-sms.axelio.app') ? email : undefined;

  // Use SECURITY DEFINER RPC that handles phone normalization server-side,
  // bypassing RLS issues for phone-only B2B clients.
  const { data: rpcResult, error: rpcError } = await supabase
    .rpc('resolve_b2b_client_session', {
      p_email: effectiveEmail ?? null,
      p_phone: phone ?? null,
    });

  if (rpcError) {
    console.error('[b2bMarketplace] resolve_b2b_client_session RPC error:', rpcError.message);
    throw new Error(`Failed to resolve B2B session: ${rpcError.message}`);
  }

  const result = rpcResult as { clientRecords: B2bClientRecord[]; companies: { id: string; legal_name: string }[] } | null;
  const records = result?.clientRecords ?? [];

  if (records.length === 0) {
    return null;
  }

  const allowedCompanyIds = [...new Set(records.map(r => r.company_id))];

  const pricelistByCompany: Record<string, string | null> = {};
  for (const r of records) {
    pricelistByCompany[r.company_id] = r.pricelist_id;
  }

  return {
    clientRecords: records,
    allowedCompanyIds,
    pricelistByCompany,
    companies: result?.companies ?? [],
  };
}

// ─── Field Agent resolution ───────────────────────────────────────────────────

/**
 * resolveAgentSession — Phase 16 upgrade
 *
 * Called when `resolveB2bSession` returns null — checks:
 *   1. company_members  → confirms the user is an internal ERP member.
 *   2. inventory_agents → finds the agent's own profile row by matching
 *                         email OR phone (same lookup used for B2B clients).
 *                         This is the row whose `id` is stored as `agent_id`
 *                         on inventory_clients rows.
 *
 * Returns null when the user has no company_members row (unknown user).
 * Returns an AgentSession with agentProfileId = null when a company_members
 * row exists but no inventory_agents row matches (edge case: admin user).
 *
 * @param userId  auth.users.id (required — used for company_members lookup)
 * @param email   authenticated user's email (optional — for agent lookup)
 * @param phone   authenticated user's phone (optional — for agent lookup)
 */
export async function resolveAgentSession(
  userId: string,
  email?: string | null,
  phone?: string | null,
): Promise<AgentSession | null> {
  // ── 1. Find inventory_agents rows by email/phone (SECURITY DEFINER RPC) ───
  //
  // This is the primary lookup. External field agents may NOT have a
  // company_members row — they only exist in inventory_agents.  The RPC
  // bypasses RLS and matches by JWT email/phone.

  let agentProfileId:           string | null = null;
  let agentName:                string | null = null;
  let agentEmail:               string | null = null;
  let agentPhone:               string | null = null;
  let agentCompanyId:           string | null = null;
  let salesCommissionRate:      number | null = null;
  let collectionCommissionRate: number | null = null;
  let defaultWarehouseId:       string | null = null;
  let isMasterAgent                           = false;
  let allAgentProfileIds:       string[]      = [];
  let agentRows: Record<string, unknown>[]    = [];

  if (email || phone) {
    const { data: rpcResult, error: rpcError } = await supabase
      .rpc('get_all_agent_rows_for_current_user');

    if (rpcError) {
      console.warn(
        '[b2bMarketplace] resolveAgentSession: get_all_agent_rows_for_current_user RPC error:',
        rpcError.message,
      );
    }

    agentRows = (rpcResult as Record<string, unknown>[] | null) ?? [];

    if (agentRows.length > 0) {
      const primaryRow = agentRows[0] as Record<string, unknown>;
      agentProfileId           = primaryRow['id'] as string;
      agentCompanyId           = (primaryRow['company_id'] as string) ?? null;
      agentName                = (primaryRow['name'] as string) ?? null;
      agentEmail               = (primaryRow['email'] as string | null) ?? null;
      agentPhone               = (primaryRow['phone'] as string | null) ?? null;
      salesCommissionRate      = (primaryRow['sales_commission_rate'] as number | null) ?? null;
      collectionCommissionRate = (primaryRow['collection_commission_rate'] as number | null) ?? null;
      defaultWarehouseId       = (primaryRow['default_warehouse_id'] as string | null) ?? null;
      isMasterAgent            = !!(primaryRow['is_master_agent']);
      allAgentProfileIds       = agentRows.map(r => r['id'] as string);
    }
  }

  // ── 2. Gate: an agent MUST have an inventory_agents row ───────────────────
  if ((email || phone) && !agentProfileId) {
    return null;
  }

  // ── 3. Optionally enrich with company_members (role, permissions) ─────────
  //
  // External agents may lack a company_members row — that's fine.  We fall
  // back to sensible defaults so the session is still valid.
  let role = 'agent';
  let modulePermissions: Record<string, string> = {};

  const { data: memberRow } = await supabase
    .from('company_members')
    .select('company_id, role, module_permissions')
    .eq('user_id', userId)
    .single();

  if (memberRow) {
    role = memberRow.role;
    modulePermissions = (memberRow.module_permissions as Record<string, string>) ?? {};
    // If agent rows exist, prefer the one matching the member's company
    if (agentRows.length > 0 && agentCompanyId !== memberRow.company_id) {
      const matchingRow = agentRows.find(r => (r.company_id as string) === memberRow.company_id);
      if (matchingRow) {
        agentProfileId  = matchingRow['id'] as string;
        agentCompanyId  = matchingRow['company_id'] as string;
        agentName       = (matchingRow['name'] as string) ?? null;
        agentEmail      = (matchingRow['email'] as string | null) ?? null;
        agentPhone      = (matchingRow['phone'] as string | null) ?? null;
        salesCommissionRate      = (matchingRow['sales_commission_rate'] as number | null) ?? null;
        collectionCommissionRate = (matchingRow['collection_commission_rate'] as number | null) ?? null;
        defaultWarehouseId       = (matchingRow['default_warehouse_id'] as string | null) ?? null;
        isMasterAgent            = !!(matchingRow['is_master_agent']);
      }
    }
  }

  // ── 4. Build per-company master-agent lookup ─────────────────────────────
  const masterAgentCompanyIds: string[] = agentRows
    .filter(r => !!(r['is_master_agent']))
    .map(r => r['company_id'] as string);
  const masterCompanySet = new Set(masterAgentCompanyIds);

  // ── 5. Fetch inventory_clients ────────────────────────────────────────────
  //
  // Phase 122 fix: check is_master_agent PER COMPANY, not globally.
  // For companies where the agent IS master → all b2b_allowed clients.
  // For companies where the agent is NOT master → only assigned clients.
  let agentClientRecords: B2bClientRecord[] = [];
  if (allAgentProfileIds.length > 0) {
    const allCompanyIds = agentRows.map(r => r['company_id'] as string);
    const masterCompanies = allCompanyIds.filter(cid => masterCompanySet.has(cid));
    const regularCompanies = allCompanyIds.filter(cid => !masterCompanySet.has(cid));

    const agentIdSet = new Set(allAgentProfileIds);

    // Fetch master-company clients (all b2b_allowed in those companies)
    if (masterCompanies.length > 0) {
      const selectCols = 'id, company_id, name, email, phone, pricelist_id, business_id, is_vat_liable, client_access, agent_id, consignment_warehouse_id, inventory_agents(name)';
      let { data: allClientRows, error: clientErr } = await supabase
        .from('inventory_clients')
        .select(selectCols)
        .in('company_id', masterCompanies)
        .eq('b2b_allowed', true)
        .order('name');

      // Retry without the nested inventory_agents join if the first attempt fails.
      // The nested join can fail when RLS on inventory_agents blocks the agent
      // from reading other agents' names (phone-only agents hit this edge case).
      if (clientErr || !allClientRows) {
        const { data: fallbackRows } = await supabase
          .from('inventory_clients')
          .select('id, company_id, name, email, phone, pricelist_id, business_id, is_vat_liable, client_access, agent_id, consignment_warehouse_id')
          .in('company_id', masterCompanies)
          .eq('b2b_allowed', true)
          .order('name');
        allClientRows = fallbackRows as unknown as typeof allClientRows;
      }

      const masterRecords = ((allClientRows ?? []) as Record<string, unknown>[]).map(row => ({
        id:            row.id as string,
        company_id:    row.company_id as string,
        name:          row.name as string,
        email:         (row.email as string | null) ?? null,
        phone:         (row.phone as string | null) ?? null,
        pricelist_id:  (row.pricelist_id as string | null) ?? null,
        business_id:   (row.business_id as string | null) ?? null,
        is_vat_liable: (row.is_vat_liable as boolean | undefined) ?? undefined,
        client_access: (row.client_access as string | undefined) ?? undefined,
        is_own_client: agentIdSet.has(row.agent_id as string),
        assigned_agent_id:   (row.agent_id as string | null) ?? null,
        assigned_agent_name: ((row as Record<string, unknown>).inventory_agents as Record<string, unknown> | null)?.name as string | null ?? null,
        consignment_warehouse_id: (row.consignment_warehouse_id as string | null) ?? null,
      }));

      masterRecords.sort((a, b) => {
        const aOwn = (a as unknown as Record<string, unknown>).is_own_client ? 0 : 1;
        const bOwn = (b as unknown as Record<string, unknown>).is_own_client ? 0 : 1;
        if (aOwn !== bOwn) return aOwn - bOwn;
        return (a.name ?? '').localeCompare(b.name ?? '', 'he');
      });
      agentClientRecords.push(...masterRecords);
    }

    // Fetch regular-company clients (only assigned to this agent)
    if (regularCompanies.length > 0) {
      const regularAgentIds = allAgentProfileIds.filter(aid => {
        const row = agentRows.find(r => (r['id'] as string) === aid);
        return row && !masterCompanySet.has(row['company_id'] as string);
      });
      if (regularAgentIds.length > 0) {
        const { data: clientRows } = await supabase
          .from('inventory_clients')
          .select('id, company_id, name, email, phone, pricelist_id, business_id, is_vat_liable, client_access, consignment_warehouse_id')
          .in('agent_id', regularAgentIds)
          .eq('b2b_allowed', true)
          .order('name');
        agentClientRecords.push(...((clientRows ?? []) as B2bClientRecord[]));
      }
    }
  }

  return {
    userId,
    companyId:                agentCompanyId ?? '',
    role,
    modulePermissions,
    agentProfileId,
    agentName,
    agentEmail,
    agentPhone,
    salesCommissionRate,
    collectionCommissionRate,
    defaultWarehouseId,
    isMasterAgent,
    masterAgentCompanyIds,
    agentClientRecords,
  };
}

// ─── Route classifier ─────────────────────────────────────────────────────────

export type UserType = 'b2b_client' | 'field_agent' | 'dual_role' | 'unknown';

export interface ClassifiedSession {
  type:          UserType;
  b2bSession?:   B2bClientSession;
  agentSession?: AgentSession;
}

/**
 * classifyAuthenticatedUser
 *
 * Single entry-point for the mobile app's post-OTP routing.
 * Returns a discriminated union describing who the user is and
 * providing the data needed to bootstrap their session.
 *
 * Usage in the mobile app's auth callback:
 * ```ts
 * const result = await classifyAuthenticatedUser(session.user);
 * switch (result.type) {
 *   case 'b2b_client':
 *     navigate('/catalog', { state: result.b2bSession });
 *     break;
 *   case 'field_agent':
 *     navigate('/agent-dashboard', { state: result.agentSession });
 *     break;
 *   case 'unknown':
 *     await supabase.auth.signOut();
 *     showError('אין לך גישה למערכת. פנה למנהל החברה.');
 *     break;
 * }
 * ```
 */
export async function classifyAuthenticatedUser(user: {
  id:    string;
  email?: string | null;
  phone?: string | null;
}): Promise<ClassifiedSession> {
  // Run both lookups in parallel so we can detect the dual-role edge case
  // (same email exists in both inventory_clients and inventory_agents).
  const [b2bSession, agentSession] = await Promise.all([
    resolveB2bSession(user.email ?? undefined, user.phone ?? undefined),
    resolveAgentSession(user.id, user.email ?? undefined, user.phone ?? undefined),
  ]);

  // Dual-role: user is both a registered B2B client AND a field agent.
  // The caller (B2BLoginPage) will show a Role Selector screen.
  if (b2bSession && agentSession) {
    return { type: 'dual_role', b2bSession, agentSession };
  }

  if (b2bSession) {
    return { type: 'b2b_client', b2bSession };
  }

  if (agentSession) {
    return { type: 'field_agent', agentSession };
  }

  return { type: 'unknown' };
}

// ─── Catalog helper ───────────────────────────────────────────────────────────

/**
 * fetchAllowedCompanies
 *
 * Thin convenience wrapper used by the mobile catalog screen.
 * Given a logged-in user, returns the array of company IDs whose
 * products the user is allowed to see.
 *
 * For B2B clients  → returns the companies they are registered with.
 * For field agents → returns their single company (wrapped in an array).
 *
 * @example
 * ```ts
 * const companyIds = await fetchAllowedCompanies(session.user);
 *
 * const { data: products } = await supabase
 *   .from('inventory_products')
 *   .select('*')
 *   .in('company_id', companyIds)
 *   .eq('is_active', true);
 * ```
 */
export async function fetchAllowedCompanies(user: {
  id:    string;
  email?: string | null;
  phone?: string | null;
}): Promise<string[]> {
  const classified = await classifyAuthenticatedUser(user);

  switch (classified.type) {
    case 'b2b_client':
      return classified.b2bSession!.allowedCompanyIds;

    case 'field_agent':
      return [classified.agentSession!.companyId];

    default:
      return [];
  }
}

// ─── Order submission ─────────────────────────────────────────────────────────

/**
 * One line item passed to submitOrder.
 * Mirrors a CartItem but is decoupled from the React context type so
 * this service stays framework-agnostic.
 */
export interface OrderLineInput {
  product_id: string;
  requested_quantity: number;
  unit_price: number;
}

/**
 * The caller context required to write a b2b_orders row.
 *
 * For a B2B Client:
 *   - client_id  = the inventory_clients.id that matches their email/phone
 *                  AND the specific company they are ordering from.
 *   - company_id = same company.
 *   - agent_id   = undefined / null.
 *
 * For a Field Agent ordering on behalf of a client:
 *   - client_id  = the chosen client's inventory_clients.id.
 *   - company_id = the agent's company.
 *   - agent_id   = the agent's auth.users id (auth.uid()).
 */
export interface OrderContext {
  company_id: string;
  client_id:  string;
  agent_id?:  string | null;
  /** Phase 115: agent who should receive commission credit (master agent feature) */
  selling_agent_id?: string | null;
}

/** What submitOrder returns on success. */
export interface SubmittedOrder {
  order_id:          string;
  line_count:        number;
  total_amount:      number;   // denormalised value read back from the DB after triggers run
  /** Phase 46: UUID of the auto-generated delivery note. NULL when instant_delivery was false
   *  or when the company has no 'תעודת משלוח' document type configured. */
  delivery_note_id:  string | null;
}

/**
 * Phase 73: B2B order classification.
 *   • 'standard'    — normal sale (default).
 *   • 'consignment' — goods delivered on consignment terms.
 * The DB CHECK constraint on b2b_orders.order_type enforces these values,
 * and the RPC additionally rejects 'consignment' for clients whose
 * inventory_clients.allow_consignment is false.
 */
export type B2BOrderType = 'standard' | 'consignment';

/**
 * submitOrder
 *
 * Phase 46.1: Routes through the atomic `b2b_submit_order` RPC (p_lines jsonb).
 * When `isInstantDelivery=true` (Van Sale) the RPC also locks the order and
 * auto-creates a תעודת משלוח — all in one DB transaction, no race conditions.
 *
 * Phase 73: Adds an explicit `orderType` ('standard' | 'consignment') that is
 * forwarded as p_order_type. The legacy fallback path (used only when the RPC
 * is missing) writes the same column directly on the b2b_orders row.
 *
 * IMPORTANT — why p_lines must be a JSON string, not a plain array:
 *   PostgREST maps each top-level key in the POST body to a function parameter.
 *   When a parameter is `jsonb`, it expects the value to already be a JSON
 *   value (object, array, scalar).  Passing a JS array works correctly because
 *   supabase-js serialises it as a JSON array in the request body.
 *
 * Falls back to the legacy two-step insert ONLY for normal (non–instant-delivery)
 * orders when the RPC is genuinely missing (PGRST202 = migration not yet applied).
 * For instant-delivery orders, any RPC failure is surfaced as an error — we
 * never silently create a pending order when the user requested a delivery note.
 *
 * Throws a plain Error with a Hebrew-friendly message on any DB failure
 * so the caller can forward it directly to a toast.
 */
export async function submitOrder(
  ctx:                OrderContext,
  lines:              OrderLineInput[],
  isInstantDelivery = false,
  orderType: B2BOrderType = 'standard',
): Promise<SubmittedOrder> {
  if (lines.length === 0) {
    throw new Error('העגלה ריקה — לא ניתן לשלוח הזמנה ללא מוצרים');
  }

  // ── Phase 46.4 + Phase 73: atomic RPC path ─────────────────────────────────
  const rpcPayload = {
    p_company_id:       ctx.company_id,
    p_client_id:        ctx.client_id,
    p_agent_id:         ctx.agent_id ?? null,
    p_lines:            lines.map(l => ({
                          product_id:         l.product_id,
                          requested_quantity: l.requested_quantity,
                          unit_price:         l.unit_price,
                        })),
    p_instant_delivery: isInstantDelivery,
    /* Phase 73 */
    p_order_type:       orderType,
    /* Phase 115 */
    p_selling_agent_id: ctx.selling_agent_id ?? null,
  };

  console.log(
    '[submitOrder] calling b2b_submit_order, instant=', isInstantDelivery,
    'order_type=', orderType,
    'payload=', JSON.stringify(rpcPayload),
  );

  const { data: rpcResult, error: rpcErr } = await supabase.rpc(
    'b2b_submit_order',
    rpcPayload,
  );

  if (rpcErr) {
    console.error(
      '[submitOrder] RPC ERROR — code:', rpcErr.code,
      '| message:', rpcErr.message,
      '| details:', rpcErr.details,
      '| hint:', rpcErr.hint,
    );

    // Any error from the RPC must be surfaced — never swallow silently.
    // Only fall back to legacy path when the function literally does not exist
    // (PGRST202 = migration not yet applied) AND it is not a Van Sale order.
    const rpcMissing = rpcErr.code === 'PGRST202';

    if (isInstantDelivery || !rpcMissing) {
      // Van Sale: never fall back — always throw so the agent sees the error.
      // Non-Van-Sale with real DB error: also throw.
      throw new Error(`שגיאה ביצירת ההזמנה (${rpcErr.code}): ${rpcErr.message}`);
    }
    // Non-Van-Sale + PGRST202 only: fall through to legacy path.
  }

  if (!rpcErr && rpcResult) {
    const row = rpcResult as { order_id: string; total_amount: number; delivery_note_id: string | null };
    console.log('[submitOrder] RPC success — order_id:', row.order_id, 'delivery_note_id:', row.delivery_note_id);
    return {
      order_id:         row.order_id,
      line_count:       lines.length,
      total_amount:     row.total_amount ?? 0,
      delivery_note_id: row.delivery_note_id ?? null,
    };
  }

  // ── Legacy fallback path (two sequential inserts) ──────────────────────────
  // Reached ONLY when the RPC is missing (PGRST202) AND isInstantDelivery=false.
  const orderPayload: {
    company_id: string;
    client_id:  string;
    agent_id?:  string | null;
    order_type: B2BOrderType;
  } = {
    company_id: ctx.company_id,
    client_id:  ctx.client_id,
    /* Phase 73 — direct write so the legacy path also tags the order. */
    order_type: orderType,
  };
  if (ctx.agent_id) orderPayload.agent_id = ctx.agent_id;

  const { data: orderRow, error: orderErr } = await supabase
    .from('b2b_orders')
    .insert(orderPayload)
    .select('id')
    .single();

  if (orderErr || !orderRow) {
    throw new Error(
      `שגיאה ביצירת ההזמנה: ${orderErr?.message ?? 'תגובה ריקה מהשרת'}`,
    );
  }

  const order_id = orderRow.id as string;

  const linePayloads = lines.map(l => ({
    order_id,
    product_id:         l.product_id,
    requested_quantity: l.requested_quantity,
    unit_price:         l.unit_price,
  }));

  const { error: linesErr } = await supabase
    .from('b2b_order_lines')
    .insert(linePayloads);

  if (linesErr) {
    await supabase.from('b2b_orders').delete().eq('id', order_id);
    throw new Error(
      `ההזמנה נוצרה אך שמירת הפריטים נכשלה: ${linesErr.message}`,
    );
  }

  const { data: finalOrder } = await supabase
    .from('b2b_orders')
    .select('total_amount')
    .eq('id', order_id)
    .single();

  return {
    order_id,
    line_count:        lines.length,
    total_amount:      (finalOrder?.total_amount as number) ?? 0,
    delivery_note_id:  null,  // legacy path never creates a delivery note
  };
}

// ─── Session → OrderContext helper ────────────────────────────────────────────

/**
 * resolveOrderContext
 *
 * Converts the router navigation state (set by B2BLoginPage after OTP) into
 * the OrderContext needed by submitOrder.
 *
 * For a B2B client the `clientRecords` array may span multiple companies
 * (multi-vendor client).  We pick the record whose company_id matches the
 * first product in the cart; if the cart has products from a single company
 * that is unambiguous.  Multi-company carts are not yet supported — the
 * catalog already filters by allowed companies, so in practice the cart will
 * always be single-company unless the UX explicitly adds a company switcher.
 *
 * For an agent the context is straightforward: their own company + auth uid.
 *
 * Returns null when context cannot be determined (user not recognised).
 */
export function resolveOrderContext(
  navState: B2bClientSession | AgentSession | null | undefined,
  _authUserId: string,          // reserved for agent flow (Phase 4 agent dashboard)
  cartCompanyId: string,        // company_id of the first cart item's product
): OrderContext | null {
  if (!navState) return null;

  // ── B2B Client path ────────────────────────────────────────────────────────
  if ('clientRecords' in navState) {
    // Find the inventory_clients row for the specific company being ordered from
    const record = navState.clientRecords.find(
      r => r.company_id === cartCompanyId,
    );
    if (!record) {
      // Fallback: take the first record if single-company client
      const first = navState.clientRecords[0];
      if (!first) return null;
      return {
        company_id: first.company_id,
        client_id:  first.id,
        agent_id:   null,
      };
    }
    return {
      company_id: record.company_id,
      client_id:  record.id,
      agent_id:   null,
    };
  }

  // ── Field Agent path ───────────────────────────────────────────────────────
  // Phase 4 note: agents will eventually select the client from a list.
  // For now we cannot resolve client_id from agent state alone — the agent
  // dashboard (B2BAgentPage) will pass the chosen client_id explicitly.
  // This path is a guard; it will be wired up fully in the agent flow.
  if ('companyId' in navState) {
    // Agent context — client_id not yet known from this state shape.
    // Return null to surface a clear error rather than insert a wrong row.
    return null;
  }

  return null;
}
